import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Plus, BookOpen, Loader2, Check, GraduationCap } from 'lucide-react';

const SUBJECTS = [
  'Computer Science', 'Biology', 'Chemistry', 'Physics', 'Mathematics',
  'Engineering', 'Medicine', 'Business', 'Psychology', 'Literature', 'History', 'General'
];

const AddToCourseModal = ({ documentId, documentTitle, onClose, onSuccess }) => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState('select'); // 'select' or 'create'
  const [selectedCourseId, setSelectedCourseId] = useState('');

  // New course form
  const [newName, setNewName] = useState('');
  const [newSubject, setNewSubject] = useState('General');
  const [newCode, setNewCode] = useState('');

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const fetchMyCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses/my`);
      setCourses(res.data.courses || []);
      if ((res.data.courses || []).length === 0) {
        setMode('create');
      }
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedCourseId) {
      toast.error('Please select a course');
      return;
    }
    setSubmitting(true);
    try {
      const course = courses.find(c => c.id === selectedCourseId);
      const formData = new FormData();
      formData.append('course_name', course.name);
      formData.append('course_code', course.code || '');
      formData.append('subject', course.subject || 'General');
      formData.append('document_type', 'Study Guide');
      await axios.post(`${API}/courses/assign/${documentId}`, formData);
      toast.success(`Added to "${course.name}"`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add to course');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAndAssign = async () => {
    if (!newName.trim()) {
      toast.error('Course name is required');
      return;
    }
    setSubmitting(true);
    try {
      // Create the course
      const formData = new FormData();
      formData.append('name', newName.trim());
      formData.append('subject', newSubject);
      formData.append('code', newCode.trim());
      const createRes = await axios.post(`${API}/courses/create`, formData);
      const course = createRes.data.course;

      // Assign document to it
      const assignData = new FormData();
      assignData.append('course_name', course.name);
      assignData.append('course_code', course.code || '');
      assignData.append('subject', course.subject || 'General');
      assignData.append('document_type', 'Study Guide');
      await axios.post(`${API}/courses/assign/${documentId}`, assignData);

      toast.success(`Created "${course.name}" and added document`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create course');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[85vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="add-to-course-modal"
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Add to Course</h3>
              <p className="text-xs text-gray-500 truncate max-w-[250px]">{documentTitle}</p>
            </div>
          </div>
          <Button onClick={onClose} variant="ghost" size="icon" className="rounded-xl" data-testid="close-course-modal">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Body */}
        <div className="p-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Mode Tabs */}
              {courses.length > 0 && (
                <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-5">
                  <button
                    data-testid="tab-existing-course"
                    onClick={() => setMode('select')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      mode === 'select' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <BookOpen className="w-4 h-4 inline mr-1.5" />
                    Existing Course
                  </button>
                  <button
                    data-testid="tab-new-course"
                    onClick={() => setMode('create')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      mode === 'create' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Plus className="w-4 h-4 inline mr-1.5" />
                    New Course
                  </button>
                </div>
              )}

              {mode === 'select' && courses.length > 0 ? (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">Select a course</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {courses.map(course => (
                      <button
                        key={course.id}
                        data-testid={`course-option-${course.id}`}
                        onClick={() => setSelectedCourseId(course.id)}
                        className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                          selectedCourseId === course.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{course.name}</p>
                            <p className="text-xs text-gray-500">
                              {course.code && <span className="font-mono mr-2">{course.code}</span>}
                              {course.subject} · {course.document_count || 0} docs
                            </p>
                          </div>
                          {selectedCourseId === course.id && (
                            <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  <Button
                    data-testid="assign-to-course-btn"
                    onClick={handleAssign}
                    disabled={!selectedCourseId || submitting}
                    className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 mt-2"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
                    ) : (
                      <><Check className="w-4 h-4 mr-2" /> Add to Course</>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {courses.length === 0 && (
                    <p className="text-sm text-gray-500 mb-1">No courses yet. Create your first one:</p>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
                    <Input
                      data-testid="new-course-name"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      placeholder="e.g. Organic Chemistry"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                      <Select value={newSubject} onValueChange={setNewSubject}>
                        <SelectTrigger className="rounded-xl" data-testid="new-course-subject">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBJECTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Course Code</label>
                      <Input
                        data-testid="new-course-code"
                        value={newCode}
                        onChange={e => setNewCode(e.target.value)}
                        placeholder="e.g. CHEM301"
                        className="rounded-xl"
                      />
                    </div>
                  </div>
                  <Button
                    data-testid="create-and-assign-btn"
                    onClick={handleCreateAndAssign}
                    disabled={!newName.trim() || submitting}
                    className="w-full rounded-xl bg-blue-600 hover:bg-blue-700"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-2" /> Create Course & Add</>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddToCourseModal;
